import { ref, watch, onUnmounted, type Ref, computed } from 'vue';
import { useSdk } from './useSdk';
import type { UserProfile } from '@cc-consulting-nv/ccsdk';

interface Subscription {
  unsubscribe: () => void;
}

// Avoid refetching the same profile repeatedly across many PostCards.
// This is intentionally module-scoped so it dedupes across component instances.
const inFlightFetches = new Map<string, Promise<void>>();
const lastFetchAtMs = new Map<string, number>();
const FETCH_TTL_MS = 5 * 60 * 1000;

/**
 * Composable to reactively observe user profile from IndexedDB cache.
 * Uses Dexie's liveQuery to automatically update when the user data changes in IndexedDB.
 * This means if the user profile is updated anywhere in the app, all components
 * using this composable will automatically reflect the changes.
 *
 * @param userId - Reactive ref containing the user ULID to observe
 * @returns Reactive user profile, loading state, and error
 *
 * @example
 * ```typescript
 * const userId = computed(() => post.value?.userId);
 * const { userProfile, isLoading } = useUserProfile(userId);
 * // userProfile will automatically update when the user's data changes in IndexedDB
 * ```
 */
export function useUserProfile(userId: Ref<string | undefined>) {
  const { sdk } = useSdk();
  const userProfile = ref<UserProfile | null>(null);
  const isLoading = ref(false);
  const error = ref<Error | null>(null);

  let currentSubscription: Subscription | null = null;

  async function ensureUserCached(id: string): Promise<void> {
    const lastFetchAt = lastFetchAtMs.get(id);
    if (lastFetchAt && Date.now() - lastFetchAt < FETCH_TTL_MS) {
      return;
    }

    const existing = inFlightFetches.get(id);
    if (existing) {
      return existing;
    }

    const promise = (async () => {
      try {
        await sdk.fetchUserProfileById(id);
      } finally {
        lastFetchAtMs.set(id, Date.now());
        inFlightFetches.delete(id);
      }
    })();

    inFlightFetches.set(id, promise);
    return promise;
  }

  /**
   * Set up reactive subscription to user profile changes in IndexedDB
   */
  async function subscribeToUser(id: string) {
    // Clean up previous subscription
    if (currentSubscription) {
      currentSubscription.unsubscribe();
      currentSubscription = null;
    }

    if (!id) {
      userProfile.value = null;
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      // First, ensure the user is in the cache (fetch if needed)
      await ensureUserCached(id);

      // Then subscribe to reactive updates from IndexedDB
      const observable = await sdk.observeUserProfile(id);

      currentSubscription = observable.subscribe({
        next: (profile: UserProfile | null) => {
          userProfile.value = profile;
          isLoading.value = false;
        },
        error: (err: unknown) => {
          console.error('[useUserProfile] Subscription error:', err);
          error.value = err as Error;
          isLoading.value = false;
        },
      });
    } catch (err) {
      console.error('[useUserProfile] Failed to subscribe to user:', err);
      error.value = err as Error;
      userProfile.value = null;
      isLoading.value = false;
    }
  }

  // Watch for userId changes and set up new subscription
  watch(
    userId,
    (newId) => {
      if (newId) {
        subscribeToUser(newId);
      } else {
        // Clean up when userId becomes undefined
        if (currentSubscription) {
          currentSubscription.unsubscribe();
          currentSubscription = null;
        }
        userProfile.value = null;
      }
    },
    { immediate: true }
  );

  // Clean up subscription on component unmount
  onUnmounted(() => {
    if (currentSubscription) {
      currentSubscription.unsubscribe();
      currentSubscription = null;
    }
  });

  return {
    userProfile,
    isLoading,
    error,
  };
}

/**
 * Convenience composable that extracts common user display properties
 * with proper null handling and fallbacks.
 *
 * @param userId - Reactive ref containing the user ULID to observe
 * @param fallbackUser - Optional embedded user data to use as fallback
 */
export function useReactiveUserDisplay(
  userId: Ref<string | undefined>,
  fallbackUser?: Ref<{ name?: string; displayName?: string; username?: string; avatar?: string; avatarUrl?: string } | undefined>
) {
  const { userProfile, isLoading, error } = useUserProfile(userId);

  const displayName = computed(() => {
    if (userProfile.value) {
      return userProfile.value.displayName ||
        (userProfile.value as Record<string, unknown>).name as string | undefined;
    }
    return fallbackUser?.value?.displayName || fallbackUser?.value?.name;
  });

  const username = computed(() => {
    return userProfile.value?.username || fallbackUser?.value?.username;
  });

  const avatar = computed(() => {
    if (userProfile.value) {
      return userProfile.value.avatarUrl ||
        (userProfile.value as Record<string, unknown>).avatar as string | undefined;
    }
    return fallbackUser?.value?.avatarUrl || fallbackUser?.value?.avatar;
  });

  return {
    userProfile,
    displayName,
    username,
    avatar,
    isLoading,
    error,
  };
}
